import { axiosToken, simpleAxios } from "@/utils/http";
import type { AxiosResponse } from "axios";
import { defineStore, acceptHMRUpdate } from "pinia"
import { useContentStore } from "./content";
import { type MusicalBingoData } from "./interfaces/musical_bingo_interface";
import { compressPlayerMediaForUpload } from "@/utils/playerMediaUpload";

export const useMusicalBingoStore = defineStore("musical_bingo", {
	state: () => (
		{
			musicalBingoData: {} as MusicalBingoData,
			teams: [],
			current_musical_bingo_team: {} as any,
			bingo_player: null as HTMLAudioElement | null,
			bingo_challenge_player: null as HTMLAudioElement | null,
			video_player: null as HTMLVideoElement | null,
			photo_player: [] as any[],
			current_photo: 0 as number,
			volume: 10 as number,
			last_volume: 10 as number,
			challenge_files: [] as any[],
			challenge_post_files: [] as any[],
			player_online_volume: 10 as number,
		}
	),
	actions: {
		async skip_challenge(api_key:string) {
			return await simpleAxios("post", "musicalBingo/skip_challenge/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async getMusicalBingoData (api_key:string)
		{
			await simpleAxios("get", "events/event_full/" + api_key, {})
			.then((response: AxiosResponse) => {
                this.musicalBingoData = response.data
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async start_game(api_key:string) {
			return await simpleAxios("post", "musicalBingo/start_game/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async next_step(api_key:string, data:object = {}) {
			return await simpleAxios("post", "musicalBingo/next_step/" + api_key, {
				data
			})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async send_command(api_key:string, command:string) {
			await simpleAxios("post", "musicalBingo/command/" + api_key , {
				command: command
			})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async start_bingo(api_key:string, set_id:number, excluded_teams:number[] = []) {
			return await simpleAxios("post", "musicalBingo/start_bingo/" + api_key , {
				set_id: set_id,
				excluded_teams: excluded_teams
			})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async mark_song(api_key:string, correct:boolean, song_id?: number) {
			return await simpleAxios("post", "musicalBingo/send_bingo_data/" + api_key , {
				id_team: this.current_musical_bingo_team.id,
				correct: correct,
				...(song_id != null ? { song_id } : {})
			})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async send_answer(api_key:string, answer:string) {
			return await simpleAxios("post", "musicalBingo/send_challenge_data/" + api_key , {
				answer: answer,
				id_team: this.current_musical_bingo_team.id,
			})
		},
		async get_challenge_photo(api_key:string) {
			return await simpleAxios("get", "musicalBingo/get_challenge_photo/" + api_key, {})
			.then((response: AxiosResponse) => {
				this.photo_player = response.data
			})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async send_photo(api_key:string, photo:Blob, name:string) {
			const file = await compressPlayerMediaForUpload(photo, name)
			const formData = new FormData()
			formData.append("photo", file, file.name)
			formData.append("id_team", this.current_musical_bingo_team.id.toString())
			return await simpleAxios("post", "musicalBingo/send_challenge_photo/" + api_key , formData)
		},
		set_bingo_player(player:HTMLAudioElement) {
			this.bingo_player = player
		},
		pause_song() {
            if (this.bingo_player) {
                this.bingo_player.pause()
				if (this.bingo_challenge_player && this.get_event_type == 'challenge') {
					this.volume = 3
					this.bingo_challenge_player.play()
					this.bingo_challenge_player.volume = this.volume / 10
				}
            }
        },

        resume_song() {
            if (this.bingo_player) {
				if (this.bingo_challenge_player && this.get_event_type == 'challenge') {
					this.volume = 10
					this.bingo_challenge_player.pause()
					this.bingo_player.volume = this.volume / 10
				}
                this.bingo_player.play()
            }
        },

        repeat_song() {
            if (this.bingo_player) {
                this.bingo_player.currentTime = 0
                // this.bingo_player.play()
            }
			if (this.bingo_challenge_player) {
				this.bingo_challenge_player.currentTime = 0
				// this.bingo_challenge_player.play()
			}
        },
		volume_up() {
			if(this.volume < 10) {
				this.volume++
				this.last_volume = this.volume
			}
			if(this.bingo_player) {
				this.bingo_player.volume = this.volume / 10
				if (this.bingo_challenge_player) {
					this.bingo_challenge_player.volume = this.volume / 10
				}
			}
			
		},
		volume_down() {
			if(this.volume > 0) {
				this.volume--
				this.last_volume = this.volume
			}
			if(this.bingo_player) {
				this.bingo_player.volume = this.volume / 10
				if (this.bingo_challenge_player) {
					this.bingo_challenge_player.volume = this.volume / 10
				}
			}
		
		},
        updateTeam(data:any) {

			if( this.teams.length == 0)
			{
				return
			}
			console.log(data)
            const team = this.teams.find(team => team.team.id == data.id)
            if(team) 
			{
                team.team = data
			}
			
			if( this.current_musical_bingo_team && team.team.id == this.current_musical_bingo_team.team.id)
			{
				this.current_musical_bingo_team.team = data
			}
        },
		update_photo_team( data:object) {
			const photo_player = this.photo_player.find(photo => photo.id == data.id)
			if(photo_player) {
				this.photo_player.forEach(photo => {
					if (photo.id === data.id) {
						photo.photo = data.photo;
						console.log("..............photo")
						}
					});	
				}
			else {
				this.photo_player.push(data)
			}
		},
		async finish_bingo(api_key:string) {
			return await simpleAxios("post", "musicalBingo/finish_bingo/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async set_final_ranking(api_key:string) {
			return await simpleAxios("post", "musicalBingo/final_ranking/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async set_final_photo(api_key:string) {
			return await simpleAxios("post", "musicalBingo/final_image/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		async set_bino_menu(api_key:string) {
			return await simpleAxios("post", "musicalBingo/select_bingo_menu/" + api_key, {})
			.catch((e: any) => {
				console.log(e);
				return null
			});
		},
		
	},
	persist: {
		// paths: ["events"]
	},
	getters:{
		get_event_type: (state) => {
			try {
				
				if (state.musicalBingoData.current_bingo_order.length == 0) {
					return null
				}
				const event = state.musicalBingoData.current_bingo_order[state.musicalBingoData.bingo_count]
	
	
				return event?.type
			} catch (error) {
				return null
			}
		},
		get_current_song: (state) => {
			try {
				const contentStore = useContentStore()
				const event = state.musicalBingoData.current_bingo_order[state.musicalBingoData.bingo_count]
				console.log(event)
				if (event?.type == 'song') {	
					return contentStore.content.musical_bingo_song.find(song => song.id == event.id)
				}
			} catch (error) {
				return null
			}
			return null
		},
		get_current_challenge: (state) => {
			try {
				const contentStore = useContentStore()
				const event = state.musicalBingoData.current_bingo_order[state.musicalBingoData.bingo_count]
				if (event?.type == 'challenge') {	
					return contentStore.content.musical_bingo_challenge.find(challenge => challenge.id == event.id)
				}
				return null
			} catch (error) {
				return null
			}
		},
		get_team_by_id: (state) => (id:number) => {
			try {
				return state.teams.find(team => team.id == id) ?? { team: { name: '' } }
			} catch (e) {
				return { team: { name: '' } }
			}
		},
		get_winner: (state) => {
			const winner_team =[]
			for (const team of state.teams) {
				if (team.data_bingo == undefined || !Array.isArray(team.data_bingo)) {
					continue
				}
				let winner = true
				for (const data of team.data_bingo	) {
					if ( data.status != 'checked') {
						winner = false
					}
				}
				if (winner) {
					winner_team.push(team.id)
				}
			}
			return winner_team
		}
		
		
	}
})


if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useMusicalBingoStore, import.meta.hot))
}
